import { useEffect, useRef, useState } from 'react';

import debounce from '../../utils/debounce';
import { getPlaceDetails } from '../../api/places-client';
import { useGoogleMapsScript } from '../../utils/google-maps-script';

import { normalizeLocalityToCityId } from './utils';

const SessionToken = () => {
  let token = new window.google.maps.places.AutocompleteSessionToken();

  const renew = () =>
    (token = new window.google.maps.places.AutocompleteSessionToken());

  const getToken = () => token;

  const getTokenAsString = () => token.xn;

  return {
    renew,
    getToken,
    getTokenAsString
  };
};

const useAutocompleteService = ({
  countryCode,
  boundCoords,
  pointOfInterest,
  onPlaceSelected
}) => {
  const autocompleteSessionToken = useRef(null);
  const autocompleteService = useRef(null);
  const placesRef = useRef(null);
  const [inputValue, setInputValue] = useState(pointOfInterest?.name);
  const [suggestionList, setSuggestionList] = useState([]);
  const [suggestionDescriptions, setSuggestionDescriptions] = useState([]);
  const placesMapRef = useRef({});

  const closeSuggestions = e => {
    if (placesRef.current && !placesRef.current.contains(e.target)) {
      setSuggestionList([]);
      setSuggestionDescriptions([]);
    }
  };

  const handleLoadGoogleMapsScript = () => {
    autocompleteSessionToken.current = SessionToken();
    autocompleteService.current =
      new window.google.maps.places.AutocompleteService();
  };

  useEffect(() => {
    document.addEventListener('click', closeSuggestions);

    return () => document.removeEventListener('click', closeSuggestions);
  }, []);

  useGoogleMapsScript(handleLoadGoogleMapsScript);

  useEffect(() => {
    setInputValue(pointOfInterest ? pointOfInterest.name : '');
  }, [pointOfInterest]);

  const requestSuggestions = useRef(
    debounce(value => {
      if (value.length > 2 && autocompleteService.current) {
        autocompleteService.current.getPlacePredictions(
          {
            input: value,
            radius: '50000',
            location: new window.google.maps.LatLng({
              lat: parseFloat(boundCoords.lat),
              lng: parseFloat(boundCoords.lng)
            }),
            sessionToken: autocompleteSessionToken.current.getToken(),
            componentRestrictions: {
              country: countryCode
            }
          },
          results => {
            if (results && results.length > 0) {
              // Crear un mapa de descripciones a objetos de Google Places
              const newPlacesMap = {};
              results.forEach(result => {
                newPlacesMap[result.description] = result;
              });
              placesMapRef.current = newPlacesMap;
              setSuggestionList(results || []);
              setSuggestionDescriptions(results.map(r => r.description));
            } else {
              placesMapRef.current = {};
              setSuggestionList([]);
              setSuggestionDescriptions([]);
            }
          }
        );
      } else {
        placesMapRef.current = {};
        setSuggestionList([]);
        setSuggestionDescriptions([]);
      }
    }, 300)
  );

  const handleInput = e => {
    const value = e?.target?.value ?? e;
    setInputValue(value);
    requestSuggestions.current(value);
  };

  const setSelectedValue = placeSelected => {
    setInputValue(placeSelected.description || placeSelected.name);
    setSuggestionList([]);
    setSuggestionDescriptions([]);
    placesMapRef.current = {};

    onPlaceSelected(placeSelected);
  };

  const handleSelect = (placeId, description) => async () => {
    if (placeId) {
      const response = await getPlaceDetails(
        placeId,
        'geometry,address_component',
        autocompleteSessionToken.current.getTokenAsString()
      );
      const address = response.data.result.address_components;
      // eslint-disable-next-line camelcase
      const { long_name } =
        address.find(({ types }) => types.includes('locality')) || {};

      const cityId = normalizeLocalityToCityId(long_name);

      setSelectedValue({
        placeId,
        cityId,
        ...response.data.result.geometry.location,
        name: description
      });

      autocompleteSessionToken.current.renew();
    }
  };

  const handlePlaceSelected = async selectedDescription => {
    const placeResult = placesMapRef.current[selectedDescription];

    if (placeResult && placeResult.place_id) {
      const response = await getPlaceDetails(
        placeResult.place_id,
        'geometry,address_component',
        autocompleteSessionToken.current.getTokenAsString()
      );
      const address = response.data.result.address_components;
      // eslint-disable-next-line camelcase
      const { long_name } =
        address.find(({ types }) => types.includes('locality')) || {};

      const cityId = normalizeLocalityToCityId(long_name);

      const selectedPlace = {
        placeId: placeResult.place_id,
        cityId,
        ...response.data.result.geometry.location,
        name: selectedDescription
      };

      setInputValue(selectedDescription);
      setSuggestionList([]);
      setSuggestionDescriptions([]);
      placesMapRef.current = {};

      onPlaceSelected(selectedPlace);
      autocompleteSessionToken.current.renew();
    }
  };

  // Para compatibilidad con PlacesAutocompleteRedesign
  const handleInputChange = handleInput;

  return {
    handleInput,
    handleInputChange,
    suggestionList,
    suggestionDescriptions,
    inputValue,
    placesRef,
    handleSelect,
    handlePlaceSelected,
    placesMapRef
  };
};

export default useAutocompleteService;
